import React from 'react';
import { Spinner, Typography } from '@/components/common';
import {
	ConversationHeader,
	ConversationPagination,
	ConversationThread,
	ConversationWriteMessage,
	LayoutInbox,
} from '@/components/dashboard';
import { inboxService } from '@/api/inbox';
import { Conversation, ConversationList } from '@/interfaces/inbox';
import { useRouter } from 'next/router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
	GET_CONVERSATION_INFO,
	GET_CONVERSATIONS,
	GET_INBOX_LIST,
} from '@/api';
import AppRoutes from '@/const/routes';
import { useEventSource } from '@/hooks/useEventSource';
import { useSession } from 'next-auth/react';

export const ConversationScreen: React.FC = () => {
	const session = useSession();
	const token = session.data?.accessToken;

	const { data: eventResponse } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/new-message/subscribe?token=${token}`,
	});

	//router
	const router = useRouter();
	const { id, page, index, limit, ai_enabled } = router.query;

	// pagination
	const containerRef = React.useRef<any>(null);
	const prevScrollPos = React.useRef(0);
	const [currentPage, setCurrentPage] = React.useState<number>(
		Number(index) || 0
	);
	const [totalPages, setTotalPages] = React.useState<number>(0);
	const [conversation, setConversation] = React.useState<Conversation[]>([]);

	// formulate  to get the page
	const handleIndexToSearchConversation = (): number => {
		return (Number(page) - 1) * Number(limit) + Number(index) + 1;
	};

	const { data: conversationId } = useQuery({
		queryKey: [GET_INBOX_LIST, currentPage],
		queryFn: () =>
			inboxService.getInboxList({
				page: currentPage,
				limit: 1,
				search: '',
				caseId: '',
			}),
		enabled: !!currentPage,
		refetchOnWindowFocus: false,
	});

	const fetchData = async ({
		pageParam = 1,
	}: {
		pageParam?: number;
	}): Promise<ConversationList> => {
		const response = await inboxService.getConversations({
			caseId: '',
			clientId: '',
			conversationId: (id as string) || '',
			page: pageParam,
			limit: 10,
		});
		return response.data;
	};

	const {
		data: conversationPaginated,
		fetchNextPage,
		refetch,
		hasNextPage,
		isFetchingNextPage,
		isError,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_CONVERSATIONS, id],
		queryFn: fetchData,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
		enabled: !!id,
	});

	const { data: conversationInfo } = useQuery({
		queryKey: [GET_CONVERSATION_INFO, id],
		queryFn: () => inboxService.getConversationInfo((id as string) || ''),
		enabled: !!id,
		refetchOnWindowFocus: false,
	});

	// ----------- Use effects ----------------//
	React.useEffect(() => {
		if (conversationId) {
			setTotalPages(conversationId.data.totalPages);
			router.push({
				pathname: `${AppRoutes.CLIENTAPP_INBOX}/${conversationId.data.items[0].id}`,
				query: { page, limit, index, ai_enabled },
			});
		}
	}, [conversationId]);

	React.useEffect(() => {
		if (page && index) {
			const newPage = handleIndexToSearchConversation();
			setCurrentPage(newPage);
		}
	}, [page, index]);

	React.useEffect(() => {
		const container = containerRef.current;
		const scrollToBottom = () => {
			if (container) {
				container.scrollTop = container.scrollHeight;
			}
		};
		// Call function to scroll to end only on first load
		if (
			conversationPaginated &&
			conversationPaginated.pageParams.length === 1
		) {
			setTimeout(() => {
				scrollToBottom();
			}, 10);
		} else if (conversationPaginated) {
			containerRef.current.scrollTop = prevScrollPos.current;
		}

		const handleScroll = () => {
			const { scrollTop, clientHeight } = container;
			let scrollCurrent = clientHeight;

			if (conversationPaginated) {
				scrollCurrent = clientHeight * conversationPaginated.pageParams.length;
			}

			if (scrollTop === 0 && hasNextPage) {
				prevScrollPos.current = scrollCurrent;
				fetchNextPage();
			}
		};

		container?.addEventListener('scroll', handleScroll);

		return () => {
			container?.removeEventListener('scroll', handleScroll);
		};
	}, [conversationPaginated, containerRef]);

	React.useEffect(() => {
		const container = containerRef.current;

		const scrollToBottom = () => {
			if (container) {
				container.scrollTop = container.scrollHeight;
			}
		};

		if (eventResponse) {
			refetch();
			scrollToBottom();
		}
	}, [eventResponse, refetch]);

	React.useEffect(() => {
		const revertConversation =
			conversationPaginated?.pages.reverse().flatMap((page) => page.items) ||
			[];
		setConversation(revertConversation);
	}, [conversationPaginated]);

	// ------------- Functions -------------------//

	const handleNextConversation = (): void => {
		searchConversationId('next');
	};

	const handlePrevConversation = (): void => {
		searchConversationId('prev');
	};

	const searchConversationId = (type: 'next' | 'prev'): void => {
		let currentPage = Number(page);
		let indexCurrent = Number(index);

		if (type === 'next' && currentPage <= totalPages) {
			if (indexCurrent + 1 >= Number(limit)) {
				indexCurrent = 0; // forward to the next page
				currentPage += 1;
			} else {
				indexCurrent += 1;
			}
		}
		if (type === 'prev' && currentPage + 1 > 0) {
			if (indexCurrent < 0) {
				currentPage -= 1;
				indexCurrent = 0; // return to the prev page
			} else {
				indexCurrent -= 1;
			}
		}

		router.push({
			pathname: `${AppRoutes.CLIENTAPP_INBOX}/${id}`,
			query: { page: currentPage, limit, index: indexCurrent, ai_enabled },
		});
	};

	return (
		<LayoutInbox showChatAIOnly={ai_enabled === 'true'}>
			<div className="flex flex-col bg-grey-2 px-6 lg:px-8 py-2 lg:py-6 rounded-2xl h-full overflow-y-auto scroll-custom">
				<ConversationPagination
					index={`${currentPage}`}
					totalPages={totalPages}
					prevPath={AppRoutes.CLIENTAPP_INBOX}
					handleNextConversation={handleNextConversation}
					handlePrevConversation={handlePrevConversation}
				/>
				<ConversationHeader
					caseTitle={conversationInfo?.data.case.title || ''}
					category={conversationInfo?.data.case.category.name}
					subject={conversationInfo?.data.subject.title || ''}
					to={conversationInfo?.data.to}
					from={conversationInfo?.data.from}
					date={conversationInfo?.data.createdAt || ''}
					isYourLawyer={conversationInfo?.data.isYourLawyer}
				/>
				{isError ? (
					<div className="flex justify-center items-center w-full h-full">
						<Typography type="semibold-14" className="my-10">
							Unexpected Error
						</Typography>
					</div>
				) : (
					<>
						{isFetchingNextPage && (
							<div className="flex items-center justify-center w-full gap-2">
								<Typography type="regular-12">Loading...</Typography>
								<Spinner />
							</div>
						)}
						<ConversationThread
							conversations={conversation ? conversation : []}
							containerRef={containerRef}
						/>
						<ConversationWriteMessage
							conversationInfo={conversationInfo?.data}
							id={id as string}
							refetch={refetch}
						/>
					</>
				)}
			</div>
		</LayoutInbox>
	);
};
